import { useUrl } from '@/hooks/use-url';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import TplExamplesValueProposalFooter from '../tpl-examples-value-proposal-footer';
import Renderer from './Renderer';

type Props = {
  site: any;
  page: any;
  showHeadersNav: boolean;
  showHeader: boolean;
  showBreadcrumb: boolean;
  showSummary: boolean;
  showAuthor: boolean;
  showDateUpdate: boolean;
  showTitleInContent: boolean;
  showFooter: boolean;
};

export default function Article({
  site,
  page,
  showHeadersNav,
  showHeader,
  showBreadcrumb,
  showSummary,
  showAuthor,
  showDateUpdate,
  showTitleInContent,
  showFooter,
}: Props) {
  if (!site || !page) {
    return <div></div>;
  }

  const [activeHeadersNav, setActiveHeadersNav] = useState(false);
  const [isAnchorsOpen, setIsAnchorsOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
  const { getAppUrl } = useUrl();
  const router = useRouter();

  const handleAnchorChange = (anchorSlug: string | null) => {
    setActiveAnchor(anchorSlug);

    if (anchorSlug === null) {
      window.scrollTo(0, 0);
      history.replaceState(null, '', window.location.href.split('#')[0]);
    }
  };
  const toggleAnchors = () => {
    setIsAnchorsOpen(!isAnchorsOpen); // basculer l'état de l'accordéon
  };

  return (
    <article data-template={page.template}>
      {showHeadersNav && (
        <div className={'headers-nav' + (activeHeadersNav ? ' active' : '')}>
          <div className="headers-nav-container">
            {site.tpl_page_summary && page.content_headers && page.content_headers.length > 0 && (
              <div className={'headers-nav-title'} onClick={() => setActiveHeadersNav(!activeHeadersNav)}>
                <p className="text">{site.tpl_page_summary}</p>
                <div className="trigger">
                  <img alt="caret up" src="/assets/icons/caret-up-purple.svg" width="24" height="24" loading="lazy" />
                </div>
              </div>
            )}
            {page.content_headers && page.content_headers.length > 0 && (
              <ul>
                {page.content_headers.map((header: any) => {
                  return (
                    <li key={header.slug}>
                      <Link href={`#${header.slug}`}>
                        <span className="text">{header.text}</span>
                        <img
                          className="icon"
                          alt="chevron down"
                          src="/assets/icons/chevron-down-purple.svg"
                          width="20"
                          height="20"
                          loading="lazy"
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="article-container">
        {showHeader && ['Page', 'Auteur'].includes(page.template) && (
          <header>
            <div className="inner">
              {showBreadcrumb && (
                <nav aria-label="Breadcrumb" className="breadcrumb">
                  <ul>
                    {!page.parent && (
                      <li>
                        <span>Guides</span>
                      </li>
                    )}
                    {page.parent && (
                      <li>
                        {!page.parent && (
                          <svg width="16" height="16" viewBox="0 0 16 16">
                            <defs>
                              <path
                                id="twemeq2xma"
                                d="M11.268 8.442l-5.061 5.061c-.245.244-.64.244-.884 0l-.59-.59c-.244-.244-.245-.639-.002-.883L8.742 8l-4.01-4.03c-.244-.244-.243-.639 0-.883l.59-.59c.245-.244.64-.244.885 0l5.06 5.061c.245.244.245.64 0 .884z"
                              />
                            </defs>
                            <g fill="none" fillRule="evenodd">
                              <g>
                                <g>
                                  <g>
                                    <g transform="translate(-94 -97) translate(22 94) translate(72 3)">
                                      <mask id="58m4acnydb" fill="#fff">
                                        <use href="#twemeq2xma" />
                                      </mask>
                                      <g fill="#61687B" mask="url(#58m4acnydb)">
                                        <path d="M0 0H16V16H0z" />
                                      </g>
                                    </g>
                                  </g>
                                </g>
                              </g>
                            </g>
                          </svg>
                        )}
                        <Link href={`https://${page.parent.hostname}${page.parent.path}`} aria-current="page">
                          {page.parent.title}
                        </Link>
                      </li>
                    )}
                    <li className="current">
                      <svg width="16" height="16" viewBox="0 0 16 16">
                        <defs>
                          <path
                            id="twemeq2xma"
                            d="M11.268 8.442l-5.061 5.061c-.245.244-.64.244-.884 0l-.59-.59c-.244-.244-.245-.639-.002-.883L8.742 8l-4.01-4.03c-.244-.244-.243-.639 0-.883l.59-.59c.245-.244.64-.244.885 0l5.06 5.061c.245.244.245.64 0 .884z"
                          />
                        </defs>
                        <g fill="none" fillRule="evenodd">
                          <g>
                            <g>
                              <g>
                                <g transform="translate(-94 -97) translate(22 94) translate(72 3)">
                                  <mask id="58m4acnydb" fill="#fff">
                                    <use href="#twemeq2xma" />
                                  </mask>
                                  <g fill="#61687B" mask="url(#58m4acnydb)">
                                    <path d="M0 0H16V16H0z" />
                                  </g>
                                </g>
                              </g>
                            </g>
                          </g>
                        </g>
                      </svg>
                      <span>{page.title}</span>
                    </li>
                  </ul>
                </nav>
              )}

              <div className="article-intro">
                <div className="article-intro-a">
                  <h1 className="title">{page.title}</h1>

                  <div className="article-intro-inner">
                    <div>
                      {page.authorData && page.authorData.linkedin && (
                        <p className="intro">
                          {page.authorData.job}
                          <br />
                          <Link className="linkedin-profile" href={page.authorData.linkedin} rel="nofollow">
                            <img src="/images/logo/linkedin.png" width="24" height="24" alt="Linkedin" loading="lazy" />
                          </Link>
                        </p>
                      )}

                      {showAuthor && (
                        <div className="page-infos">
                          {page.author && (
                            <div className="author">
                              <div className="author-picture">
                                {page.author.image && page.author.image.url && (
                                  <img
                                    src={page.author.image.url ? page.author.image.url : null}
                                    alt={page.author.fullname}
                                    width={60}
                                    height={60}
                                  />
                                )}
                              </div>

                              <div className="author-data">
                                <address>
                                  {page.author.url && (
                                    <span className="fullname">
                                      <Link href={page.author.url}>{page.author.fullname}</Link>
                                    </span>
                                  )}
                                  {!page.author.url && <span className="fullname">{page.author.fullname}</span>}
                                </address>
                                {page.date_last_update && (
                                  <time dateTime={page.date_last_update_YYYY_MM_DD}>
                                    {site.content_section_update_title} {page.date_last_update_DD_MM_YYYY}
                                  </time>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {showDateUpdate && (
                        <div className="page-infos">
                          <div className="page-date-update">
                            <time dateTime={page.date_last_update_YYYY_MM_DD}>
                              {site.content_section_update_title}{' '}
                              {site?.html_lang === 'de'
                                ? page.date_last_update_DD_MM_YYYY.replace(/\//g, '.')
                                : page.date_last_update_DD_MM_YYYY}
                            </time>
                          </div>
                        </div>
                      )}

                      {page.content_intro && (
                        <p className="intro" dangerouslySetInnerHTML={{ __html: page.content_intro }}></p>
                      )}
                    </div>

                    {page.pageTopImage && (
                      <div className="article-illustration">
                        {page.template == 'Article' &&
                          page.pageTopImage.url &&
                          page.pageTopImage.width &&
                          page.pageTopImage.height && (
                            <img
                              alt={
                                page.pageTopImage.alt && page.pageTopImage.alt.trim().length > 0
                                  ? page.pageTopImage.alt
                                  : page.title
                              }
                              src={page.pageTopImage.url}
                              width={page.pageTopImage.width / 2}
                              height={page.pageTopImage.height / 2}
                            />
                          )}
                        {page.template == 'Auteur' &&
                          page.pageTopImage.url &&
                          page.pageTopImage.width &&
                          page.pageTopImage.height && (
                            <img
                              alt={page.title}
                              src={page.pageTopImage.url}
                              width={Math.round(page.pageTopImage.width / 2)}
                              height={Math.round(page.pageTopImage.height / 2)}
                            />
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </header>
        )}

        {showHeader && ['Article'].includes(page.template) && (
          <header>
            <div className="relative flex flex-col gap-5 overflow-hidden bg-[#0E2053] p-5 md:items-center lg:mb-10 lg:rounded-2xl">
              {showBreadcrumb && (
                <nav aria-label="Breadcrumb" className="m-0 p-0 text-[#A7B0C3] lg:pb-1 lg:pt-3">
                  <ul className="w-auto text-sm">
                    {!page.parent && (
                      <li>
                        <span>Guides</span>
                      </li>
                    )}
                    {page.parent && (
                      <li>
                        {!page.parent && (
                          <svg width="16" height="16" viewBox="0 0 16 16">
                            <defs>
                              <path
                                id="twemeq2xma"
                                d="M11.268 8.442l-5.061 5.061c-.245.244-.64.244-.884 0l-.59-.59c-.244-.244-.245-.639-.002-.883L8.742 8l-4.01-4.03c-.244-.244-.243-.639 0-.883l.59-.59c.245-.244.64-.244.885 0l5.06 5.061c.245.244.245.64 0 .884z"
                              />
                            </defs>
                            <g fill="none" fillRule="evenodd">
                              <g>
                                <g>
                                  <g>
                                    <g transform="translate(-94 -97) translate(22 94) translate(72 3)">
                                      <mask id="58m4acnydb" fill="#fff">
                                        <use href="#twemeq2xma" />
                                      </mask>
                                      <g fill="#61687B" mask="url(#58m4acnydb)">
                                        <path d="M0 0H16V16H0z" />
                                      </g>
                                    </g>
                                  </g>
                                </g>
                              </g>
                            </g>
                          </svg>
                        )}
                        <Link
                          className="text-white hover:text-white"
                          href={`https://${page.parent.hostname}${page.parent.path}`}
                          aria-current="page"
                        >
                          {page.parent.title}
                        </Link>
                      </li>
                    )}
                    <li className="current">
                      <svg width="16" height="16" viewBox="0 0 16 16">
                        <defs>
                          <path
                            id="twemeq2xma"
                            d="M11.268 8.442l-5.061 5.061c-.245.244-.64.244-.884 0l-.59-.59c-.244-.244-.245-.639-.002-.883L8.742 8l-4.01-4.03c-.244-.244-.243-.639 0-.883l.59-.59c.245-.244.64-.244.885 0l5.06 5.061c.245.244.245.64 0 .884z"
                          />
                        </defs>
                        <g fill="none" fillRule="evenodd">
                          <g>
                            <g>
                              <g>
                                <g transform="translate(-94 -97) translate(22 94) translate(72 3)">
                                  <mask id="58m4acnydb" fill="#fff">
                                    <use href="#twemeq2xma" />
                                  </mask>
                                  <g fill="#61687B" mask="url(#58m4acnydb)">
                                    <path d="M0 0H16V16H0z" />
                                  </g>
                                </g>
                              </g>
                            </g>
                          </g>
                        </g>
                      </svg>
                      <span>{page.title}</span>
                    </li>
                  </ul>
                </nav>
              )}

              <h1 className="header m-0 block p-0 text-center leading-snug text-white lg:px-[250px] lg:text-[38px]">
                {page.title}
              </h1>

              {page.product_type && (site.article_header_cta_cv || site.article_header_cta_lm) && (
                <div className="flex justify-center lg:pb-4">
                  <Link href={getAppUrl(`/create/resume?ref=${router.asPath}`)}>
                    <button
                      type="button"
                      className="relative flex h-[54px] min-w-[160px] animate-ctaPulse items-center justify-center rounded-md bg-[#4B4FF2] px-6 text-base font-medium text-white hover:cursor-pointer"
                    >
                      {site.article_header_cta_cv}
                    </button>
                  </Link>
                </div>
              )}

              {['Article'].includes(page.template) && showAuthor && (
                <div className="page-infos lg:hidden">
                  {page.author && (
                    <div className="flex items-center justify-center pb-2 lg:pb-4">
                      <div className="author-picture !h-12 !w-12 rounded-full border border-white">
                        {page.author.image && page.author.image.url && (
                          <img
                            src={page.author.image.url ? page.author.image.url : null}
                            alt={page.author.fullname}
                            width={60}
                            height={60}
                          />
                        )}
                      </div>

                      <div className="author-data text-white">
                        <address>
                          {page.author.url && (
                            <span className="fullname !text-sm">
                              <Link href={page.author.url}>{page.author.fullname}</Link>
                            </span>
                          )}
                          {!page.author.url && <span className="fullname !text-sm">{page.author.fullname}</span>}
                        </address>
                        {page.date_last_update && (
                          <time className="!text-sm !text-white/50" dateTime={page.date_last_update_YYYY_MM_DD}>
                            {site.content_section_update_title} {page.date_last_update_DD_MM_YYYY}
                          </time>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <img
                src="/assets/images/backgrounds/article-header-decoration.svg"
                height={250}
                width={250}
                className="absolute bottom-0 right-0 hidden max-h-[80%] lg:block"
              />
            </div>
          </header>
        )}

        {showSummary && page.content_anchors && page.content_anchors.length > 0 && ['Page'].includes(page.template) && (
          <div className="anchors">
            <ul aria-label={site.content_section_go_to_title}>
              {page.content_anchors.map((anchor: any) => {
                return (
                  <li key={anchor.slug}>
                    <Link href={`#${anchor.slug.replace(/,/g, '')}`}>{anchor.text_alt || anchor.text}</Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className={['Article'].includes(page.template) ? 'm-auto max-w-[1131px] grid-cols-3 lg:grid' : ''}>
          {showAuthor && (
            <div className="page-infos hidden lg:order-2 lg:col-span-2 lg:row-span-1 lg:block lg:px-10">
              {page.author && (
                <div className="author lg:border-b lg:border-gray-200 lg:pb-5">
                  <div className="author-picture">
                    {page.author.image && page.author.image.url && (
                      <img
                        src={page.author.image.url ? page.author.image.url : null}
                        alt={page.author.fullname}
                        width={60}
                        height={60}
                      />
                    )}
                  </div>

                  <div className="author-data">
                    <address>
                      {page.author.url && (
                        <span className="fullname">
                          <Link href={page.author.url}>{page.author.fullname}</Link>
                        </span>
                      )}
                      {!page.author.url && <span className="fullname">{page.author.fullname}</span>}
                    </address>
                    {page.date_last_update && (
                      <time dateTime={page.date_last_update_YYYY_MM_DD}>
                        {site.content_section_update_title} {page.date_last_update_DD_MM_YYYY}
                      </time>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {page.content_anchors && page.content_anchors.length > 0 && ['Article'].includes(page.template) && (
            <div className="p-5 lg:order-1 lg:col-span-1 lg:row-span-2 lg:p-0">
              <div
                data-state={isAnchorsOpen ? 'opened' : 'closed'}
                className={`z-20 flex flex-col rounded-lg bg-[#ECF1FD] data-[state=opened]:pb-3 lg:sticky lg:top-10 lg:mb-7 lg:ml-10 lg:bg-transparent`}
              >
                <div
                  className={`flex items-center justify-between px-5 py-3 hover:cursor-pointer lg:pt-4`}
                  onClick={() => toggleAnchors()}
                >
                  <div className="font-[Spoof] text-base font-semibold lg:text-base lg:text-gray-500">
                    {site.content_section_go_to_title}
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="14"
                    fill="none"
                    viewBox="0 0 16 14"
                    className={`transition duration-500 ${isAnchorsOpen ? '-rotate-180' : ''} lg:opacity-50`}
                  >
                    <path
                      stroke="#061231"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M12 4.75l-4.5 4.5L3 4.75"
                    ></path>
                  </svg>
                </div>
                <ul
                  aria-label={site.content_section_go_to_title}
                  className={`flex flex-col overflow-hidden px-0 transition-[max-height] duration-300 ease-out lg:px-5 ${
                    isAnchorsOpen ? 'max-h-screen lg:max-h-0' : 'max-h-0 lg:max-h-screen'
                  }`}
                >
                  <li data-state={activeAnchor === null ? 'active' : ''}>
                    <span
                      data-state={activeAnchor === null ? 'active' : ''}
                      className="block px-5 py-1 text-sm text-gray-500 hover:cursor-pointer data-[state=active]:bg-[#ECF1FD] data-[state=active]:text-indigo-600 lg:mb-1 lg:rounded-lg lg:px-3 lg:py-1.5 lg:text-[15px] lg:leading-6"
                      onClick={() => handleAnchorChange(null)}
                    >
                      {site.content_breadcrumb_introduction_title}
                    </span>
                  </li>
                  {page.content_anchors.map((anchor) => {
                    return (
                      <li key={anchor.slug} data-state={anchor.slug === activeAnchor ? 'active' : ''}>
                        <Link
                          data-state={anchor.slug === activeAnchor ? 'active' : ''}
                          className="block px-5 py-1 text-sm text-gray-500 data-[state=active]:bg-[#ECF1FD] data-[state=active]:text-indigo-600 lg:mb-1 lg:rounded-lg lg:px-3 lg:py-1.5 lg:text-[15px] lg:leading-6"
                          href={`#${anchor.slug.replace(/,/g, '')}`}
                          onClick={() => handleAnchorChange(anchor.slug)}
                        >
                          {anchor.text_alt || anchor.text}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}

          <div
            className={
              ['Article'].includes(page.template)
                ? 'content max-w-[754px] lg:order-3 lg:col-span-2 lg:row-span-1'
                : 'content max-w-[844px]'
            }
          >
            {showTitleInContent && <h1 className="header block">{page.title}</h1>}

            {page.content_intro && <p className="intro" dangerouslySetInnerHTML={{ __html: page.content_intro }}></p>}

            <Renderer content_raw={page.content_raw ? JSON.parse(page.content_raw) : null} site={site} />
          </div>
        </div>

        {['Article', 'Exemples'].includes(page.template) && site.tpl_examples_value_proposal_footer && (
          <TplExamplesValueProposalFooter site={site} />
        )}

        {showFooter && (
          <footer>
            {page.authors && (
              <section className="authors" aria-label={site.content_authors.title}>
                <div className="authors-inner">
                  {page.authors &&
                    page.authors.map((author) => {
                      return (
                        <div className="author" key={author.fullname}>
                          {author && author.image && (
                            <div className="author-picture">
                              <img
                                src={author.image.url ? author.image.url : null}
                                alt={author.fullname}
                                width={author.image.width / 2}
                                height={author.image.height / 2}
                                loading="lazy"
                              />
                            </div>
                          )}

                          <div className="author-data">
                            <address>
                              <div className="profile">
                                {!author.url && <span className="fullname">{author.fullname}</span>}
                                {author.url && (
                                  <span className="fullname">
                                    <Link href={author.url}>{author.fullname}</Link>
                                  </span>
                                )}
                                <span className="role">{author.job}</span>
                              </div>
                              {author.description && <p className="bio">{author.description}</p>}
                            </address>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </section>
            )}

            {page.linked_pages && page.linked_pages.length > 0 && (
              <section className="linked-pages">
                <p className="head">
                  <b>{site.content_section_linked_pages.title}</b>
                  <br />
                  <span>{site.content_section_linked_pages.subtitle}</span>
                </p>

                <ol>
                  {page.linked_pages.map((page: any, i: number) => {
                    if (page.url) {
                      return (
                        <li key={page.title}>
                          <Link href={page.url} className="title">
                            <span className="title">{page.title}</span>
                          </Link>
                          <Link href={page.url}>
                            <span className="button">{site.content_section_linked_pages.cta}</span>
                          </Link>
                        </li>
                      );
                    }
                  })}
                </ol>
              </section>
            )}
          </footer>
        )}
      </div>
    </article>
  );
}
